import type { AdminFinanceStats } from '../../../domain/superadmin/types';
import { formatCurrency } from '../../../utils/formatters';
import { cn } from '@/lib/utils';

type FinanceKpiRowProps = {
  kpis: AdminFinanceStats['kpis'];
};

function KpiCard({
  label,
  value,
  subtitle,
  valueClassName,
}: {
  label: string;
  value: string;
  subtitle?: string;
  valueClassName?: string;
}) {
  return (
    <li className="relative overflow-hidden rounded-xl border border-border bg-surface px-s4 py-3.5">
      <span
        className="absolute inset-x-0 top-0 h-0.5 rounded-t-xl bg-primary"
        aria-hidden
      />
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.07em] text-muted">
        {label}
      </p>
      <p
        className={cn(
          'min-w-0 break-words font-mono text-[18px] font-semibold leading-tight tracking-[-0.5px] tabular-nums text-default sm:text-[22px] md:text-[26px]',
          valueClassName,
        )}
      >
        {value}
      </p>
      {subtitle && <p className="mt-1 text-[11.5px] text-muted">{subtitle}</p>}
    </li>
  );
}

export function FinanceKpiRow({ kpis }: FinanceKpiRowProps) {
  const pastDueLabel =
    kpis.subscriptionsPastDue === 1
      ? '1 inadimplente'
      : `${kpis.subscriptionsPastDue} inadimplentes`;

  return (
    <section aria-label="Indicadores financeiros" className="mb-s5">
      <ul className="grid grid-cols-1 gap-s3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="MRR atual"
          value={formatCurrency(kpis.mrrCents / 100, 'BRL')}
          subtitle="Receita recorrente mensal"
        />
        <KpiCard
          label="Receita anual projetada"
          value={formatCurrency(kpis.projectedAnnualCents / 100, 'BRL')}
          subtitle="MRR × 12"
        />
        <KpiCard
          label="Assinaturas ativas"
          value={String(kpis.subscriptionsActive)}
          subtitle={pastDueLabel}
        />
        <KpiCard
          label="Valor em risco"
          value={formatCurrency(kpis.atRiskMrrCents / 100, 'BRL')}
          subtitle="MRR de inadimplentes"
          valueClassName={kpis.atRiskMrrCents > 0 ? 'text-warning-text' : undefined}
        />
      </ul>
    </section>
  );
}
